import React, { useState, useContext, useEffect, useRef } from 'react';
import { StateContext } from '../../../StateProvider';
import { useNavigate } from 'react-router-dom';
import { useElements } from '@stripe/react-stripe-js';
import Popup from '../../../Utilities/components/Popup';
import CardDisplay from '../../../Utilities/components/CardDisplay/CardDisplay';
import { processStripePayment } from '../../../Utilities/api';
import { sendVerificationCode, verifyTwilioCode, updateUserPhoneNumber, updateWalletBalance } from '../../../Utilities/api';
import formatPhoneForDisplay from '../../../Utilities/functions/FormatPhoneForDisplay';
import './Checkout.css';
import sanitizeDate from '../../../Utilities/functions/SanitizeDate';
import { WhiteBackButton, PaymentMethodViewButton } from '../../../Utilities/components/Buttons';
import addCardIcon from '../../../../assets/Dashboard/Profile/components/Wallet/addCardIcon.png';

const Checkout = () => {
  const navigate = useNavigate();
  const elements = useElements();
  const submittingRef = useRef(false);
  const { user, cart, cartTotal, paymentMethods, setLoading, refreshUserData } =
    useContext(StateContext);

  function num(n) { return Number(n || 0); }
  function money(n) { return Number(n || 0).toFixed(2); }
  function cents(n) { return Math.round(Number(n || 0) * 100); }

  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [useBalance, setUseBalance] = useState(false);
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [defaultMethod, setDefaultMethod] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState('');
  const [walletInput, setWalletInput] = useState('');
  const [paymentErrorMessage, setPaymentErrorMessage] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoLabel, setPromoLabel] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);

  const MIN_CHARGE_USD = 0.5;

  useEffect(() => {
    if (user && user.info) {
      setEmail(user.info.email || '');
      setPhoneNumber(
        user.info.phone ? formatPhoneForDisplay(user.info.phone) : ''
      );
    }

    if (paymentMethods && user) {
      let foundDefault = null;
      if (paymentMethods.length > 0) {
        let i = 0;
        while (i < paymentMethods.length) {
          const method = paymentMethods[i];
          if (
            method.stripePaymentMethodId &&
            user.defaultPaymentMethodId &&
            String(method.stripePaymentMethodId) ===
              String(user.defaultPaymentMethodId)
          ) {
            foundDefault = method;
            break;
          }
          i = i + 1;
        }
        if (!foundDefault) foundDefault = paymentMethods[0];
      }
      setDefaultMethod(foundDefault);
      setStripeCustomerId(user.stripeCustomerId || '');
    }

    if (user && user.wallet) setRemainingBalance(num(user.wallet.balance));
    else setRemainingBalance(0);
  }, [user, paymentMethods]);

  // ---------- DETECT MEMBERSHIP / BUNDLE ----------
  function hasMembership(cart) {
    if (!cart || !Array.isArray(cart)) return false;
    let i = 0;
    while (i < cart.length) {
      const bt =
        cart[i] && cart[i].bookingType
          ? String(cart[i].bookingType).toLowerCase()
          : '';
      if (bt.indexOf('membership') !== -1) return true;
      i++;
    }
    return false;
  }

  function hasBundle(cart) {
    if (!cart || !Array.isArray(cart)) return false;
    var i = 0;
    while (i < cart.length) {
      var item = cart[i];
      var bt = "";
      if (item && item.bookingType) bt = String(item.bookingType).toLowerCase();
      if (bt.indexOf("bundle") !== -1) return true;
      i = i + 1;
    }
    return false;
  }

  function getSafeWalletAmount(input, remaining, maxTotal) {
    const requested = Math.max(0, num(input));
    return Math.min(requested, remaining, maxTotal);
  }

  function handleToggleUseBalance() {
    if (hasMembership(cart)) {
      showPaymentPopup('Wallet credits cannot be applied to membership purchases.');
      return;
    }

    setUseBalance(function (prev) {
      var next = !prev;
      if (!next) setWalletInput('');
      return next;
    });
  } 

  function getPromoDiscountDelta(cart) {
    var total = 0;
    if (!cart) return 0;

    var i = 0;
    while (i < cart.length) {
      var it = cart[i];
      if (it && it.discountSource === 'promo') {
        total += num(it.originalPrice) - num(it.discountedPrice);
      }
      i++;
    }

    return Number(total.toFixed(2));
  }

  function computeMembershipDiscountAfterBundle(user, cart) {
    if (!user || !cart) return 0;

    // get membership %
    var membershipPercent = 0;
    if (user.clubMembership &&
        user.clubMembership.billingStatus === 'active') {
      if (user.clubMembership.planType === 'Plus') membershipPercent = 20;
      if (user.clubMembership.planType === 'Club') membershipPercent = 10;
    }

    if (membershipPercent <= 0) return 0;

    // find daycare item
    var daycareItem = null;
    var i = 0;
    while (i < cart.length) {
      var bt = cart[i].bookingType
        ? String(cart[i].bookingType).toLowerCase()
        : '';
      if (bt.indexOf('daycare') !== -1) {
        daycareItem = cart[i];
        break;
      }
      i++;
    }

    if (!daycareItem) return 0;

    // total days
    var bi = daycareItem.bookingInfo || {};
    var dr = bi.dateRange;
    if (!Array.isArray(dr) || dr.length !== 2) return 0;

    var start = new Date(dr[0]);
    var end = new Date(dr[1]);
    start.setHours(0,0,0,0);
    end.setHours(0,0,0,0);

    var totalDays =
      Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;

    if (totalDays <= 0) return 0;

    // bundle days
    var bundleDays = 0;
    if (user.daycareBundle && Array.isArray(user.daycareBundle)) {
      var j = 0;
      while (j < user.daycareBundle.length) {
        var b = user.daycareBundle[j];
        if (b && b.status === 'active') {
          bundleDays = b.remainingDays || 0;
          break;
        }
        j++;
      }
    }

    var paidDays = Math.max(0, totalDays - bundleDays);
    if (paidDays <= 0) return 0;

    var perDay =
      Number(daycareItem.originalPrice || 0) / totalDays;

    var paidPortion = perDay * paidDays;

    return Number(
      (paidPortion * (membershipPercent / 100)).toFixed(2)
    );
  }

  // ---------- MEMBERSHIP DISCOUNT INFO ----------
  function getMembershipDiscountInfo(user) {
    if (!user) return { percent: 0, label: '' };

    let membership = null;
    if (user.clubMembership) membership = user.clubMembership;
    else if (user.ClubMemberships && user.ClubMemberships.length > 0)
      membership = user.ClubMemberships[0];

    if (membership &&
      membership.billingStatus === 'active' &&
      !membership.cancelledAt
    ) {
      if (membership.planType === 'Plus')
        return { percent: 20, label: 'Pawsible Plus Club' };
      if (membership.planType === 'Club')
        return { percent: 10, label: 'Pawsible Club' };
    }

    return { percent: 0, label: '' };
  }

  function getCartSums(items) {
    var originalSum = 0;
    var discountedSum = 0;

    if (items && Array.isArray(items)) {
      var i = 0;
      while (i < items.length) {
        var it = items[i] || {};
        originalSum += num(it.originalPrice);
        // discountedPrice is authoritative
        discountedSum += (it.discountedPrice != null ? num(it.discountedPrice) : num(it.originalPrice));
        i = i + 1;
      }
    }

    return { originalSum: originalSum, discountedSum: discountedSum };
  }

  function getDiscountDelta(cart) {
    var original = 0;
    var discounted = 0;

    if (cart && Array.isArray(cart)) {
      var i = 0;
      while (i < cart.length) {
        var it = cart[i] || {};
        original += num(it.originalPrice || 0);

        if (it.discountedPrice != null) discounted += num(it.discountedPrice);
        else discounted += num(it.originalPrice || 0);

        i = i + 1;
      }
    }

    var delta = original - discounted;
    if (delta < 0) delta = 0;

    return Number(delta.toFixed(2));
  }

  function getAppliedDiscountLabel(user, bundleApplied, membershipApplied, communityApplied, bundleLabel, membershipLabel, communityLabel, membershipPercent, communityPercent) {
    // Priority: Bundle > Membership > Community
    if (bundleApplied) return bundleLabel || 'Bundle Credit';
    if (membershipApplied) return membershipLabel + (membershipPercent ? ' (' + membershipPercent + '%)' : '');
    if (communityApplied) return communityLabel + (communityPercent ? ' (' + communityPercent + '%)' : '');
    return '';
  }

  function getCommunityDiscountInfo(user) {
    if (!user || !user.userCommunities) return { percent: 0, label: '' };

    var highest = 0;
    var communityName = '';

    for (var i = 0; i < user.userCommunities.length; i++) {
      var c = user.userCommunities[i];
      var info = c && c.CommunityInfo ? c.CommunityInfo : null;

      if (info && info.discountPercent && info.discountPercent > highest) {
        highest = info.discountPercent;
        communityName = info.name || info.key || 'Community';
      }
    }

    if (highest > 0)
      return { percent: highest, label: `${communityName} Community Discount` };

    return { percent: 0, label: '' };
  }

  // ---------- BUNDLE DISCOUNT INFO ----------
  function getBundleDiscountInfo(user, cart) {
    if (!user) return { amount: 0, label: '' };
    if (!user.daycareBundle || !Array.isArray(user.daycareBundle))
      return { amount: 0, label: '' };

    var activeBundle = null;
    for (var i = 0; i < user.daycareBundle.length; i++) {
      var b = user.daycareBundle[i];
      if (b && b.status === 'active') {
        activeBundle = b;
        break;
      }
    }
    if (!activeBundle) return { amount: 0, label: '' };

    var daycareItem = null;
    if (cart && Array.isArray(cart)) {
      for (var j = 0; j < cart.length; j++) {
        var it = cart[j];
        var bt =
          it && it.bookingType ? String(it.bookingType).toLowerCase() : '';
        if (bt.indexOf('daycare') !== -1) {
          daycareItem = it;
          break;
        }
      }
    }
    if (!daycareItem) return { amount: 0, label: '' };

    var bookingInfo = daycareItem.bookingInfo || {};
    var dateRange = bookingInfo.dateRange || [];
    var totalDays = 1;

    if (Array.isArray(dateRange) && dateRange.length === 2) {
      var start = new Date(dateRange[0]);
      var end = new Date(dateRange[1]);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      var diffDays = (end - start) / (1000 * 60 * 60 * 24);
      if (diffDays - Math.floor(diffDays) > 0.9) diffDays = Math.floor(diffDays);
      totalDays = Math.round(diffDays) + 1;
      if (totalDays < 1) totalDays = 1;
    }

    var totalPrice = Number(daycareItem.originalPrice || 0);
    var bundleDays = activeBundle.remainingDays || 0;
    if (totalDays <= 0 || bundleDays <= 0) return { amount: 0, label: '' };

    var eligibleDays = Math.min(bundleDays, totalDays);
    var perDay = totalPrice / totalDays;
    var bundleCreditValue = Math.round(perDay * eligibleDays);

    if (bundleCreditValue > 0) {
      var label =
        (activeBundle.bundleType ? activeBundle.bundleType : 'Daycare') +
        ' Bundle Credit';
      return { amount: bundleCreditValue, label: label };
    }

    return { amount: 0, label: '' };
  }

  // ---------- UI HELPERS ----------
  function showPaymentPopup(message) {
    setPaymentErrorMessage(
      message || 'Something went wrong. Please try again.'
    );
    setPopupContent('paymentError');
    setIsPopupOpen(true);
  }

  function isGroomingLine(item) {
    const bt =
      item && item.bookingType ? String(item.bookingType).toLowerCase() : '';
    return bt.indexOf('groom') !== -1;
  }

  function effectiveLinePrice(item) {
    if (
      item &&
      typeof item.discountedPrice !== 'undefined' &&
      item.discountedPrice !== null
    )
      return num(item.discountedPrice);
    if (
      item &&
      typeof item.originalPrice !== 'undefined' &&
      item.originalPrice !== null
    )
      return num(item.originalPrice);
    return 0;
  }

  // ---------- PROMO EVALUATION ----------
  function evaluatePromo(code, subtotal) {
    const normalized = String(code || '').trim().toUpperCase();
    if (normalized === 'PAWSTEST') {
      let discount = subtotal - MIN_CHARGE_USD;
      if (discount < 0) discount = 0;
      return { valid: true, label: 'PAWSTEST', discount };
    }
    if (normalized === 'PAWSFAM') {
      const raw = 0.1 * subtotal;
      const maxAllowed = Math.max(0, subtotal - MIN_CHARGE_USD);
      const discount = Math.max(0, Math.min(raw, maxAllowed));
      if (discount > 0)
        return { valid: true, label: 'PAWSFAM 10%', discount };
    }
    return { valid: false, label: '', discount: 0 };
  }

  const nRemaining = num(remainingBalance);

  var sums = getCartSums(cart);
  var cartOriginalSum = sums.originalSum;
  var cartDiscountedSum = sums.discountedSum;

  // grooming subtotal should use authoritative discounted line prices
  var groomingSubtotal = 0;
  if (cart && Array.isArray(cart)) {
    var gi = 0;
    while (gi < cart.length) {
      var it = cart[gi];
      if (isGroomingLine(it)) {
        var line = (it && it.discountedPrice != null) ? num(it.discountedPrice) : num(it.originalPrice);
        groomingSubtotal += line;
      }
      gi = gi + 1;
    }
  }

  const serviceCharge = Number((0.1 * groomingSubtotal).toFixed(2));
  const totalBeforeWallet = cartDiscountedSum + serviceCharge;

  // ---------- WALLET (USER-ENTERED, SAFE CLAMP) ----------
  const requestedWalletAmount = getSafeWalletAmount(
    walletInput,
    nRemaining,
    totalBeforeWallet
  );

  const appliedBalanceRender = useBalance ? requestedWalletAmount : 0;

  // wallet applies against item subtotal first (not service charge)
  const walletToItems = Math.min(appliedBalanceRender, cartDiscountedSum);

  var promoBasis = Math.max(0, cartDiscountedSum - walletToItems);

  const [promoBasisCache, setPromoBasisCache] = useState(0);
  useEffect(function () {
    setPromoBasisCache(promoBasis);
  }, [promoBasis]);

  function applyPromo() {
    setPromoError('');

    if (!promoCode || !String(promoCode).trim()) {
      setPromoApplied(false);
      setPromoLabel('');
      setPromoDiscount(0);
      setPromoError('Enter a code first.');
      return;
    }

    if (hasMembership(cart)) {
      showPaymentPopup('Promo codes cannot be applied to membership purchases.');
      return;
    }

    const result = evaluatePromo(promoCode, promoBasis);

    if (!result.valid) {
      setPromoApplied(false);
      setPromoLabel('');
      setPromoDiscount(0);
      setPromoError('Invalid or ineligible promo code.');
      return;
    }

    setPromoApplied(true);
    setPromoLabel(result.label);
    setPromoDiscount(result.discount);
    setPromoError('');
  }

  function clearPromo() {
    setPromoApplied(false);
    setPromoLabel('');
    setPromoDiscount(0);
    setPromoError('');
    setPromoCode('');
  }

  const finalTotalRender = Math.max(
    0,
    totalBeforeWallet - appliedBalanceRender - promoDiscount
  );

  const updatedBalanceRender =
    nRemaining - appliedBalanceRender;

  // ---------- MEMBERSHIP DISCOUNT (global, not line item) ----------
  const {
    percent: membershipDiscountPercent,
    label: membershipLabel,
  } = getMembershipDiscountInfo(user);

  let membershipDiscountAmount = 0;
  let membershipDiscountApplied = false;

  const {
    amount: bundleDiscountAmount,
    label: bundleLabel,
  } = getBundleDiscountInfo(user, cart);

  // ---------- CART SUMS (DISPLAY ONLY) ----------
  var sums2 = getCartSums(cart);
  var originalPriceSum = sums2.originalSum;
  var discountedPriceSum = sums2.discountedSum;

  membershipDiscountApplied =
    membershipDiscountPercent > 0 &&
    originalPriceSum > discountedPriceSum;

  membershipDiscountAmount = 0;

  const bundleDiscountApplied = bundleDiscountAmount > 0;

  const { percent: communityPercent, label: communityLabel } =
    getCommunityDiscountInfo(user);

  const communityDiscountApplied =
    communityPercent > 0 &&
    originalPriceSum > discountedPriceSum &&
    !membershipDiscountApplied &&
    !bundleDiscountApplied;

  const communityDiscountAmount =
    communityDiscountApplied
      ? originalPriceSum - discountedPriceSum
      : 0;

  // ---------- FORMAT BOOKING TYPE ----------
  function formatBookingType(item) {
    if (!item || !item.bookingType) return '';
    const type = String(item.bookingType);
    if (type.indexOf('Membership') !== -1) {
      const info = item.bookingInfo || {};
      const selectedPackage = info.selectedPackage || '';
      if (selectedPackage === 'Club')
        return 'Pawsible Club Membership';
      if (selectedPackage === 'Plus')
        return 'Pawsible Plus Club Membership';
      return 'Pawsible Membership';
    }
    if (type.indexOf('Gift-card-purchase') !== -1)
      return 'Gift Card';
    return type;
  }

  // ---------- SUBMIT PAYMENT ----------
  async function handleSubmit(e) {
    e.preventDefault();
    if (submittingRef.current || processing) return;

    if (!agreedToTerms || !agreedToPrivacy) {
      showPaymentPopup(
        'Please agree to the terms and privacy policy to continue.'
      );
      return;
    }

    if (
      !defaultMethod ||
      !defaultMethod.stripePaymentMethodId ||
      typeof defaultMethod.stripePaymentMethodId !== 'string' ||
      !defaultMethod.stripePaymentMethodId.startsWith('pm_')
    ) {
      showPaymentPopup(
        'Your payment method is invalid. Please update your card.'
      );
      return;
    }

    submittingRef.current = true;
    setProcessing(true);
    setLoading(true);

    try {
      const response = await processStripePayment(
        stripeCustomerId,
        defaultMethod.stripePaymentMethodId,
        cart,
        finalTotalRender,
        'usd',
        'Payment for services',
        'automatic',
        user,
        {
          useWallet: !!useBalance,
          appliedWalletCents: cents(appliedBalanceRender),
          promoCode: promoApplied ? promoCode : null,
          serviceChargeCents: cents(serviceCharge) // 🔑 REQUIRED
        }
      );

      if (response && response.status === 'succeeded') {
        await updateWalletBalance(user.uid, updatedBalanceRender);
        await refreshUserData(user.uid);

        if (hasMembership(cart)) {
          navigate('/profile/membership');
        } else if (hasBundle(cart)) {
          navigate('/store/services/daycare/booking');
        } else {
          navigate('/events');
        }
      } else {
        showPaymentPopup('Payment failed. Please try again.');
      }
    } catch (err) {
      console.error(err);
      showPaymentPopup(
        'There was an error processing your payment. Please try again.'
      );
    } finally {
      setProcessing(false);
      setLoading(false);
      submittingRef.current = false;
    }
  }

  const methodToDisplay =
    defaultMethod ||
    (paymentMethods && paymentMethods[0]) ||
    null;

  const hideNormalDiscounts = bundleDiscountApplied;

  var promoDelta = promoDiscount;
  var nonPromoDelta = getDiscountDelta(cart);

  var appliedDiscountLabel = getAppliedDiscountLabel(
    user,
    bundleDiscountApplied,
    membershipDiscountApplied,
    communityDiscountApplied,
    bundleLabel,
    membershipLabel,
    communityLabel,
    membershipDiscountPercent,
    communityPercent
  );

  return (
    <div>
      <div className="checkout-backdrop">
        <div className="container">
          <WhiteBackButton />
          <div className="checkout-container">
            <div>
              <div className="checkout-summary-container">
                <h2>Payment Summary</h2>
                <hr className="checkout-breakline" />

                {cart && Array.isArray(cart) && cart.length > 0 ? (
                  (function () {
                    var rows = [];
                    var i = 0;

                    while (i < cart.length) {
                      var item = cart[i];

                      var original = num(item && item.originalPrice ? item.originalPrice : 0);
                      var discounted = num(
                        item && item.discountedPrice != null ? item.discountedPrice : original
                      );

                      var bi = (item && item.bookingInfo) ? item.bookingInfo : {};
                      var showDate = !!bi.dateRange;
                      var showPets = bi.selectedPets && bi.selectedPets.length > 0;

                      var petsString = '';
                      if (showPets) {
                        var ps = '';
                        var pi = 0;
                        while (pi < bi.selectedPets.length) {
                          var p = bi.selectedPets[pi];
                          if (p && p.petName) {
                            if (ps.length > 0) ps += ', ';
                            ps += p.petName;
                          }
                          pi = pi + 1;
                        }
                        petsString = ps;
                      }

                      function displayRange(d) {
                        var s = sanitizeDate(d).split(' - ');
                        if (!s || s.length !== 2) return '';
                        return s[0] === s[1] ? s[0] : s[0] + ' - ' + s[1];
                      }

                      rows.push(
                        <div key={i}>
                          <div className="checkout-item-summary">
                            <div className="checkout-item-description">
                              <span className="checkout-item-service">
                                {formatBookingType(item)}
                                {showPets ? ' - ' + petsString : ''}
                                {showDate ? ' (' + displayRange(bi.dateRange) + ')' : ''}
                              </span>
                            </div>

                            {original === discounted ? (
                              <div className="checkout-item-originalPrice">
                                ${money(original)}
                              </div>
                            ) : (
                              <div className="checkout-item-originalPrice">
                                <span className="checkout-item-original-strike">
                                  ${money(original)}
                                </span>{' '}
                                ${money(discounted)}
                              </div>
                            )}
                          </div>
                        </div>
                      );

                      i = i + 1;
                    }

                    return rows;
                  })()
                ) : (
                  <p>No items in cart</p>
                )}

                {useBalance && (
                  <div className="checkout-item-discountSummary">
                    <div className="checkout-item-description">
                      <div className="checkout-item-service">
                        Wallet Credit
                      </div>
                    </div>
                    <div className="checkout-item-discountedPrice">
                      -${money(appliedBalanceRender)}
                    </div>
                  </div>
                )}

                {nonPromoDelta > 0 && appliedDiscountLabel && (
                  <div className="checkout-item-discountSummary">
                    <div className="checkout-item-service">
                      {appliedDiscountLabel}
                    </div>
                    <div className="checkout-item-discountedPrice">
                      -${money(nonPromoDelta)}
                    </div>
                  </div>
                )}

                {promoApplied && promoDiscount > 0 && (
                  <div className="checkout-item-discountSummary">
                    <div className="checkout-item-description">
                      <div className="checkout-item-service">
                        Promo ({promoLabel})
                      </div>
                    </div>
                    <div className="checkout-item-discountedPrice">
                      -${money(promoDiscount)}
                    </div>
                  </div>
                )}

                {serviceCharge > 0 && (
                  <div className="checkout-item-discountSummary">
                    <div className="checkout-item-description">
                      <div className="checkout-item-service">
                        Service Charge (Grooming Gratuity)
                      </div>
                    </div>
                    <div className="checkout-item-discountedPrice">
                      +${money(serviceCharge)}
                    </div>
                  </div>
                )}

                <hr className="checkout-breakline" />
                <div className="checkout-total-cost">
                  <strong>Total</strong> $
                  {money(finalTotalRender)}
                </div>
              </div>

              {/* WALLET SECTION */}
                <div className="checkout-payment-container">
                  <h2 className="balance-text">Use Wallet Balance</h2>

                  <div className="use-balance-option">
                    <label className="balance-text">
                      Available: ${money(remainingBalance)}
                    </label>

                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={useBalance}
                        onChange={handleToggleUseBalance}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  {useBalance && (
                    <div className="wallet-input-wrapper">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="wallet-amount-input"
                        placeholder="Enter amount"
                        value={walletInput}
                        onChange={(e) => setWalletInput(e.target.value)}
                      />

                      <div className="wallet-hint">
                        Applying <strong>${money(appliedBalanceRender)}</strong> of $
                        {money(remainingBalance)}
                      </div>
                    </div>
                  )}
                </div>

              {/* PROMO SECTION */}
              <div className="checkout-payment-container">
                <h2>Promotions</h2>
                <div className="checkout-contact-card">
                  <h3 className="checkout-card-title">Code</h3>
                  {!promoApplied ? (
                    <div className="promo-actions">
                      <input
                        type="text"
                        className="checkout-promo-input"
                        placeholder="Enter here"
                        value={promoCode}
                        onChange={(e) =>
                          setPromoCode(e.target.value)
                        }
                      />
                      <button
                        className="checkout-card-button"
                        onClick={applyPromo}
                      >
                        Apply
                      </button>
                    </div>
                  ) : (
                    <div className="promo-actions">
                      <span className="promo-applied-text">
                        Applied:{' '}
                        <strong>{promoLabel}</strong>
                      </span>
                      <button
                        className="checkout-card-button-remove"
                        onClick={clearPromo}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                {promoError && (
                  <p
                    className="checkout-card-info"
                    style={{ color: '#E63946' }}
                  >
                    {promoError}
                  </p>
                )}
              </div>

              {/* PAYMENT METHODS */}
              <div className="checkout-payment-container">
                <h2>Payment Methods</h2>

                {paymentMethods && paymentMethods.length > 0 ? (
                  (function () {
                    var output = [];
                    var i = 0;

                    while (i < paymentMethods.length) {
                      var method = paymentMethods[i];

                      var pmId = "";
                      if (method && method.stripePaymentMethodId) {
                        pmId = String(method.stripePaymentMethodId);
                      }

                      var isDefault = false;
                      if (
                        user &&
                        user.defaultPaymentMethodId &&
                        pmId === String(user.defaultPaymentMethodId)
                      ) {
                        isDefault = true;
                      }

                      output.push(
                        (function (capturedPmId, capturedMethod, capturedIsDefault) {
                          return (
                            <div
                              key={capturedPmId}
                              onClick={function () {
                                if (
                                  capturedPmId &&
                                  capturedPmId.substring(0, 3) === "pm_"
                                ) {
                                  navigate(
                                    "/profile/wallet/payment-methods/edit-payment/" +
                                      capturedPmId
                                  );
                                }
                              }}
                            >
                              <CardDisplay
                                method={capturedMethod}
                                isDefault={capturedIsDefault}
                              />
                            </div>
                          );
                        })(pmId, method, isDefault)
                      );

                      i = i + 1;
                    }

                    return output;
                  })()
                ) : (
                  <p>No payment methods found.</p>
                )}

                <hr className="wallet-payment-breakline" />

                <div className="wallet-card-display">
                  <img
                    src={addCardIcon}
                    alt="wallet-add"
                    className="wallet-card-logo"
                  />
                  <div className="wallet-card-info">
                    <div
                      className="wallet-card-number"
                      onClick={function () {
                        navigate('/profile/wallet/payment-methods/add-payment');
                      }}
                    >
                      Add Card
                    </div>
                  </div>
                </div>
              </div>

              {/* POPUP */}
              {isPopupOpen && (
                <Popup
                  isOpen={isPopupOpen}
                  handleClose={() => setIsPopupOpen(false)}
                >
                  {popupContent === 'paymentError' && (
                    <div className="profile-popup">
                      <h2 className="profile-popup-header">
                        Payment Issue
                      </h2>
                      <p className="profile-popup-message">
                        {paymentErrorMessage}
                      </p>
                      <div className="profile-popup-actions-confirmation">
                        <button
                          onClick={() =>
                            setIsPopupOpen(false)
                          }
                          className="profile-popup-update-button-ok"
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  )}
                </Popup>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CONFIRM SECTION */}
      <form
        className="checkout-form-container"
        onSubmit={handleSubmit}
      >
        <section className="checkout-policies-container">
          <div className="checkout-terms-and-conditions">
            <label className="checkout-policies-label">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) =>
                  setAgreedToTerms(e.target.checked)
                }
              />{' '}
              I agree to the{' '}
              <a
                href="/legal/terms-and-conditions"
                target="_blank"
                rel="noreferrer"
              >
                Terms & Conditions
              </a>
            </label>
            <label className="checkout-policies-label">
              <input
                type="checkbox"
                checked={agreedToPrivacy}
                onChange={(e) =>
                  setAgreedToPrivacy(e.target.checked)
                }
              />{' '}
              I agree to the{' '}
              <a
                href="/legal/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
            </label>
          </div>
        </section>

        <div className="checkout-confirmation-container">
          <button
            type="submit"
            disabled={!agreedToTerms || !agreedToPrivacy || processing}
          >
            {processing ? 'Processing…' : 'Confirm & Pay'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
